<script lang="tsx" setup>
import { animateDOM } from 'core'
import { getData, useAudioPlayer, useCaptions, useComments, useSummary } from './logic'

const data = getData()
const player = useAudioPlayer()
const comments = useComments()

const { status, transcription, activeTrack, isParagraphed, isEnabled, editor, hasSpeakers } = useCaptions()
const summary = useSummary()

const CaptionSwitcher = defineComponent({
  name: 'CaptionSwitcher',

  setup() {
    if (!summary.isValid)
      return () => <span>Transcript</span>

    enum Mode { Transcript = 'Transcript', Summary = 'Summary' }
    const activeMode = ref(Mode.Transcript)

    function toggle(mode: Mode) {
      animateDOM(() => {
        const isActive = mode === Mode.Summary
        activeMode.value = mode
        summary.isActive = isActive
      })
    }

    // Enable summary mode if speakers are present
    onMounted(() => {
      if (hasSpeakers.value)
        toggle(Mode.Summary)
    })

    return () => (
      <div class="flex-center gap-inherit">
        {Object.values(Mode).map((mode) => {
          const isActive = mode === activeMode.value

          return (
            <button
              key={mode}
              class={{ 'px-2 py-1 rounded-5px text-base bg-$color-neutral text-$color-background': isActive, 'text-base opacity-70': !isActive }}
              onClick={() => toggle(mode)}
            >
              {mode}
            </button>
          )
        })}
      </div>
    )
  }
})

function copyContent() {
  const content = summary.isActive ? summary.value : transcription.value.map(({ sentences }) => sentences.map(({ text }) => text).join(' ')).join('\n\n')

  if (content)
    return navigator.clipboard.writeText(content)
}

function commentNow() {
  comments.showDialog(player.audio.currentTime)
}
</script>

<template>
  <section class="relative" :has-captions="isEnabled">
    <div class="mb-4.5 gap-3 flex flex-wrap items-center justify-end">
      <h3
        v-if="isEnabled || status.loading"
        class="mr-auto gap-2 flex-center text-heading-6 text-$color-neutral opacity-70"
      >
        <CaptionSwitcher v-if="status.complete" />

        <template v-else>
          <Spinner size="xs" />
          Transcription is being generated...
        </template>
      </h3>

      <ClientOnly>
        <div class="view-transition-transcript-actions flex-center gap-inherit">
          <ToolTip
            v-if="status.error" content-class="bg-$color-primary! text-$color-background!"
            arrow-class="fill-$color-primary!"
            text="Transcription couldn't be generated because the audio quality was too low or the language is not supported"
          >
            <div class="size-8 flex-center rounded-full bg-$color-background-elevated">
              <Icon name="lucide:captions-off" class="size-5" />
            </div>
          </ToolTip>

          <!-- Download Button -->
          <a v-if="data.settings.allowDownload" :href="data.audio.audioUrl" download="recording.mp3" title="Download" target="_blank">
             <IconButton size="base" variant="secondary" icon="i-solar:download-minimalistic-bold-duotone" />
          </a>

          <!-- Copy Button -->
          <IconButton
            v-if="status.complete" size="base" variant="secondary" icon="i-solar-copy-bold-duotone"
            title="Copy content" @resolve-click="copyContent"
          />

          <!-- Captions editor icon -->
          <IconButton
            v-if="editor.canEdit && status.complete && !summary.isActive" size="base"
            :variant="editor.isEnabled ? 'primary' : 'secondary'" icon="i-mdi-pencil" @click="editor.toggle"
          />

          <PageListenSpeedControl />

          <!-- Comment Button -->
          <IconButton
            v-if="comments.enabled && isEnabled" size="base"
            :variant="comments.dialog.enabled ? 'primary' : 'secondary'" icon="i-solar-chat-round-line-linear"
            title="Add comment" @click="commentNow"
          />
        </div>
      </ClientOnly>
    </div>

    <template v-if="isEnabled">
      <div :class="{ 'grid gap-10': isParagraphed }">
        <!-- Summary -->
        <p v-if="summary.isActive" class="summary caption-line" v-html="summary.value" />

        <!-- Captions -->
        <PageListenCommentsCaptionParagraph
          v-for="(paragraph, i) in transcription" v-else :key="i" :data="paragraph"
          :can-edit="editor.canEdit" @edit-speaker-name="editor.setSpeakerName(paragraph)"
        >
          <span v-for="(cue, id) in paragraph.sentences" :key="id" class="caption-line">

            <Slot :class="{ 'active bg-$color-primary text-$color-background': cue.id === activeTrack }">
              <ContentModel
                v-if="editor.isEnabled" v-model="cue.text" placeholder="Write your sentence here" tag="span"
                input-class="border-none outline-none focus:underline focus:underline-offset-6 [&.active]:focus:bg-$color-neutral"
                class="[&.active]:focus-within:bg-$color-neutral" @update="editor.update(cue.id, $event)"
              />

              <PageListenCommentsCaptionText v-else :cue />
            </Slot>

            {{ ' ' }}
          </span>
        </PageListenCommentsCaptionParagraph>
      </div>

      <ClientOnly>
        <LazyIdlePageListenCommentsDialog v-if="comments.enabled" hydrate-on-idle />
      </ClientOnly>
    </template>

    <ClientOnly v-else>
      <LazyIdlePageListenCommentsList v-if="comments.enabled" hydrate-on-idle />
    </ClientOnly>
  </section>
</template>

<style scoped lang="sass">
.caption-line
  @apply text-base text-$color-neutral sm:text-lg

  transition: all 500ms
  transition-property: background-color, color

:deep(.summary)
  @apply pt-4 text-base

  & > *
    @apply mb-2

  & > *:first-child
    @apply mt-0

  // All heading tags
  & > :is(h1, h2, h3, h4, h5, h6)
    @apply mt-8 mb-4 font-medium text-$color-neutral-elevated-2

  & > h2
    @apply text-2xl

  & > h3
    @apply text-xl

  & > ul
    @apply list-disc py-4 px-8 bg-$color-background-elevated rounded-10px leading-relaxed text-sm

    li
      @apply not-last-of-type:mb-2

    li::marker
      @apply text-$color-neutral-border
</style>
